import * as React from "react";
import { Box, Button } from "@mui/material";

import { Controller, useForm } from "react-hook-form";
import { ProjectResource } from "../../types/project";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import SelectReason from "../../components/SelectReason";
import { useCancelTalentMutation } from "../../services/projectJob.service";
import BaseModal from "../../components/global/BaseModal";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalCancelTalentProps {
  open: boolean;
  onClose: () => void;
  job: ProjectResource | null;
}

const ModalCancelTalent = ({ open, onClose, job }: ModalCancelTalentProps) => {
  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [cancel, { isLoading }] = useCancelTalentMutation();

  // UI Labels
  const removeIndependentProfessionalLabel = useUILabel("project.removeIndependentProfessional");
  const selectCancelReasonLabel = useUILabel("project.selectCancelReason");
  const independentProfessionalCanceledLabel = useUILabel("project.independentProfessionalCanceled");
  const cancelLabel = useUILabel("common.cancel");
  const submitLabel = useUILabel("common.submit");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");

  const onSubmit = async (payload: any) => {
    try {
      if (!job) return;

      await cancel({
        projectId: job.project_id,
        jobId: job.id,
        reason: payload.reason,
      }).unwrap();
      toast.success(independentProfessionalCanceledLabel);
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("reason", "");
      clearErrors();
    }
  }, [job, open]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={isLoading}>
        {cancelLabel}
      </Button>
      <LoadingButton type="submit" variant="contained" loading={isLoading}>
        {submitLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={removeIndependentProfessionalLabel}
      footer={footer}
      maxWidth="xs"
    >
      <Box 
        component="form" 
        onSubmit={handleSubmit(onSubmit)} 
        sx={{ 
          p: 3,
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Controller
          name="reason"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <SelectReason
              label={selectCancelReasonLabel}
              onChange={onChange}
              error={!!errors.reason}
              helperText={!!errors.reason && thisFieldIsRequiredLabel}
              type="cancel-worker"
            />
          )}
        />
      </Box>
    </BaseModal>
  );
};
export default ModalCancelTalent;
